import { createTheme, type Theme } from '@mui/material/styles'
import { useMemo } from 'react'

const FALLBACK_PRIMARY = '#009d9d'

/**
 * Resolve the system primary colour set in `@head.latte` as
 * `--primary-color`. Falls back to the LESS-defined teal so the grid still
 * looks right if the CSS variable is missing (server-rendered tests, etc.).
 */
const resolvePrimary = (): string => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return FALLBACK_PRIMARY
	}
	const value = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim()
	return value === '' ? FALLBACK_PRIMARY : value
}

/**
 * Theme used by ReactGrid. Overrides `palette.primary` so every MUI primitive
 * inside the grid (Add button, Pick-icon button, edit icon, MenuItem focus,
 * checkbox tick) matches the project's `@color-primary` LESS variable.
 *
 * Memoised against the initially resolved colour. The grid does not re-create
 * the theme on every render — system colour changes require a page reload,
 * same as the rest of the LESS-driven UI.
 */
export const useGridTheme = (): Theme =>
	useMemo(() => {
		const primary = resolvePrimary()
		return createTheme({
			palette: {
				primary: {
					main: primary,
				},
			},
		})
	}, [])
